import { useSuspenseQuery } from '@tanstack/react-query';
import { getRouteApi } from '@tanstack/react-router';
import { useState } from 'react';
import { LevelTags } from '~/partials/Tags';
import { Button } from '~/primitives/button';
import { useList } from '~/util/hooks/useList';
import { levelsQuery } from '~/util/queries/public/levels';
import { Level } from '~/util/types/levels';

const Route = getRouteApi('/profile/user/$userid');

const EXCLUDED_TAGS = [
    '2.1',
    '2.2',
    '2.0',
    '1.9',
    '1.8',
    '1.6',
    '1.5',
    'Long',
    'NONG',
];
const EXCLUDED_TAGS_SET = new Set(EXCLUDED_TAGS);

type Suggestion = {
    level: Level;
    matchedTags: string[];
    tagScore: number;
    edelScore: number;
    totalScore: number;
};

export function SuggestedLevel() {
    const list = useList();
    const records = Route.useLoaderData({
        select: ({ profile: { records } }) => records,
    });
    const { data: levels } = useSuspenseQuery(
        levelsQuery({ list, exclude_pending: true, exclude_legacy: true }),
    );
    const [suggestedIndex, setSuggestedIndex] = useState<number>(0);
    const [posOffset, setPosOffset] = useState(0);

    const levelById = new Map<string, Level>();
    for (const level of levels) {
        levelById.set(level.id, level);
    }

    const completedLevels = new Set<Level>();
    for (const record of records) {
        const level = levelById.get(record.level.id);
        if (level) {
            completedLevels.add(level);
        }
    }

    const tagCounts = Object.create(null) as Record<string, number>;

    for (const level of completedLevels) {
        for (const tag of level.tags ?? []) {
            if (!tag || EXCLUDED_TAGS_SET.has(tag)) continue;
            tagCounts[tag] = (tagCounts[tag] ?? 0) + 1;
        }
    }

    const tagsByCount = Object.keys(tagCounts);
    tagsByCount.sort((tagA, tagB) => tagCounts[tagB] - tagCounts[tagA]);
    const topTagSet = new Set(tagsByCount.slice(0, 10));

    let positionSum = 0;
    let levelsWithPositionCount = 0;
    for (const level of completedLevels) {
        if (typeof level.position === 'number') {
            positionSum += level.position;
            levelsWithPositionCount++;
        }
    }
    const averagePosition = levelsWithPositionCount
        ? positionSum / levelsWithPositionCount
        : undefined;

    let suggested: Suggestion[] = [];
    if (topTagSet.size > 0) {
        const getSuggestionsForOffset = (currentOffset: number) => {
            const minPos =
                averagePosition != null
                    ? averagePosition + currentOffset - 20
                    : -Infinity;
            const maxPos =
                averagePosition != null
                    ? averagePosition + currentOffset + 20
                    : Infinity;

            const offsetSuggestions: Suggestion[] = [];

            for (const level of levels) {
                if (completedLevels.has(level)) continue;

                const pos = level.position;
                if (typeof pos === 'number' && (pos < minPos || pos > maxPos))
                    continue;

                let tagScore = 0;
                const matchedTags: string[] = [];
                for (const tag of level.tags ?? []) {
                    if (tag && topTagSet.has(tag)) {
                        tagScore++;
                        matchedTags.push(tag);
                    }
                }
                if (tagScore === 0) continue;

                const edelScore = level.edel_enjoyment ?? 0;
                const totalScore = tagScore * 10 + edelScore;
                if (totalScore <= 0) continue;

                offsetSuggestions.push({
                    level,
                    matchedTags,
                    tagScore,
                    edelScore,
                    totalScore,
                });
            }
            return offsetSuggestions;
        };

        // Try with current offset first
        let allSuggestions = getSuggestionsForOffset(posOffset);

        // If no suggestions found, gradually try nearby ranges
        if (allSuggestions.length === 0) {
            for (
                let offsetStep = 1;
                offsetStep <= 4 && allSuggestions.length === 0;
                offsetStep++
            ) {
                // Try easier levels
                allSuggestions = getSuggestionsForOffset(
                    posOffset + offsetStep * 25,
                );
                if (allSuggestions.length === 0) {
                    // Try harder levels
                    allSuggestions = getSuggestionsForOffset(
                        posOffset - offsetStep * 25,
                    );
                }
            }
        }

        allSuggestions.sort(
            (suggestionA, suggestionB) =>
                suggestionB.totalScore - suggestionA.totalScore,
        );
        suggested = allSuggestions.slice(0, 10);
    }

    if (records.length === 0 || levels.length === 0) {
        return (
            <p className="text-neutral-400">
                Not enough data to recommend a level.
            </p>
        );
    }

    if (topTagSet.size === 0) {
        return (
            <p className="text-neutral-400">
                Not enough tag data to recommend a level.
            </p>
        );
    }

    if (!suggested || suggested.length === 0) {
        return (
            <p className="text-neutral-400">No suitable suggestion found.</p>
        );
    }

    const finalPosition = suggested[suggestedIndex].level.position;

    return (
        <div className="mt-4 text-left bg-background-light p-4 rounded-xl shadow-lg space-y-4">
            <div className="flex flex-col gap-1">
                <LevelTags
                    showPosition
                    item={suggested[suggestedIndex].level}
                    className="inline-block text-xl sm:text-2xl font-extrabold text-primary leading-tight"
                />
                <p className="text-xs text-neutral-500">
                    Based on the tags from your most played levels, your typical
                    leaderboard range, and your EDEL preferences.
                </p>
            </div>

            <div className="text-sm text-neutral-300 space-y-1">
                <div>
                    <span className="font-semibold text-white">Position:</span>{' '}
                    #{finalPosition ?? 'N/A'}
                </div>
                <div>
                    <span className="font-semibold text-white">
                        EDEL Score:
                    </span>{' '}
                    {suggested[suggestedIndex].edelScore.toFixed(2) ?? 'N/A'}
                </div>
                <div>
                    <span className="font-semibold text-white">
                        Top Tag Matches:
                    </span>
                    <div className="flex flex-wrap gap-2 mt-1">
                        {suggested[suggestedIndex].matchedTags.length > 0
                            ? suggested[suggestedIndex].matchedTags.map(
                                  (tag) => (
                                      <span
                                          key={tag}
                                          className="bg-primary/20 text-primary px-2 py-0.5 rounded text-xs font-medium"
                                      >
                                          {tag}
                                      </span>
                                  ),
                              )
                            : 'None'}
                    </div>
                </div>
            </div>
            <div className="flex justify-center gap-x-4">
                <Button
                    variant="outline"
                    disabled={
                        !suggested.some(
                            (suggestion) =>
                                typeof suggestion.level.position === 'number' &&
                                suggestion.level.position >
                                    (averagePosition ?? 0) + posOffset,
                        )
                    }
                    onClick={() => setPosOffset(posOffset + 25)}
                >
                    Easier
                </Button>
                <Button
                    variant="outline"
                    onClick={() =>
                        setSuggestedIndex(
                            (suggestedIndex + 1) % suggested.length,
                        )
                    }
                >
                    Skip
                </Button>
                <Button
                    variant="outline"
                    disabled={
                        !suggested.some(
                            (suggestion) =>
                                typeof suggestion.level.position === 'number' &&
                                suggestion.level.position <
                                    (averagePosition ?? 0) + posOffset,
                        )
                    }
                    onClick={() => setPosOffset(posOffset - 25)}
                >
                    Harder
                </Button>
            </div>
        </div>
    );
}
